// garuda - Content Service
// Fetches markdown files and metadata from manifest.json

// Fetch a single markdown file
export const fetchMarkdownFile = async (path) => {
  try {
    const response = await fetch(`/content/${path}`);
    if (!response.ok) throw new Error("File not found");

    const content = await response.text();
    return content;
  } catch (error) {
    console.error("Error fetching markdown file:", error);
    return null;
  }
};

// Fetch manifest for a content type
export const fetchManifest = async (type) => {
  try {
    const response = await fetch(`/content/${type}/manifest.json`);
    if (!response.ok) throw new Error("Manifest not found");

    const manifest = await response.json();
    return manifest;
  } catch (error) {
    console.error("Error fetching manifest:", error);
    return null;
  }
};

// Fetch all content of a type with metadata
export const fetchAllContent = async (type) => {
  try {
    const manifest = await fetchManifest(type);
    if (!manifest || !manifest.items) return [];

    const contents = await Promise.all(
      manifest.items.map(async (item) => {
        const content = await fetchMarkdownFile(`${type}/${item.file}`);
        return {
          ...item,
          content: content || "",
          path: `${type}/${item.file}`,
        };
      }),
    );

    return contents
      .filter((c) => c.content)
      .sort((a, b) => {
        // Sort by timestamp, newest first
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
  } catch (error) {
    console.error("Error fetching all content:", error);
    return [];
  }
};

// Fetch notes with hierarchical structure
export const fetchNotes = async () => {
  try {
    const manifest = await fetchManifest("notes");
    if (!manifest || !manifest.topics) return [];

    const notesData = await Promise.all(
      manifest.topics.map(async (topic) => {
        const items = topic.items || [];
        const notes = await Promise.all(
          items.map(async (item) => {
            const content = await fetchMarkdownFile(
              `notes/${topic.name}/${item.file}`,
            );
            return {
              ...item,
              content: content || "",
              path: `notes/${topic.name}/${item.file}`,
            };
          }),
        );

        return {
          id: topic.id,
          topic: topic.name,
          notes: notes.filter((n) => n.content),
        };
      }),
    );

    return notesData;
  } catch (error) {
    console.error("Error fetching notes:", error);
    return [];
  }
};

// Fetch activities data
export const fetchActivities = async () => {
  try {
    const manifest = await fetchManifest("activities");
    if (!manifest) return { activities: [], heatmapData: {} };

    return {
      activities: manifest.activities || [],
      heatmapData: manifest.heatmapData || {},
    };
  } catch (error) {
    console.error("Error fetching activities:", error);
    return { activities: [], heatmapData: {} };
  }
};

// Fetch vault categories and files
export const fetchVault = async () => {
  try {
    const manifest = await fetchManifest("vault");
    if (!manifest || !manifest.categories) return [];

    return manifest.categories;
  } catch (error) {
    console.error("Error fetching vault:", error);
    return [];
  }
};
